import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRight, Zap } from 'lucide-react';

function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [skillLevel, setSkillLevel] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [goals, setGoals] = useState([]);

  const scenarioOptions = ['Work/Salary', 'Shopping/Bills', 'Freelance/Business', 'Personal/Family', 'Real Estate'];
  const goalOptions = ['Increase income', 'Save money', 'Build confidence', 'Master techniques', 'Career advancement'];

  const handleScenarioToggle = (scenario) => {
    setScenarios(prev => 
      prev.includes(scenario) ? prev.filter(s => s !== scenario) : [...prev, scenario]
    );
  };

  const handleGoalToggle = (goal) => {
    setGoals(prev => 
      prev.includes(goal) ? prev.filter(g => g !== goal) : [...prev, goal]
    );
  };

  const handleSubmit = async () => {
    if (!skillLevel || scenarios.length === 0 || goals.length === 0) {
      toast.error('Please complete all questions');
      return;
    }

    try {
      await API.post('/onboarding', {
        skill_level: skillLevel,
        scenarios,
        goals
      });
      toast.success('Profile created!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save onboarding');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-12">
          <Zap className="text-green-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3">Welcome, Ninja!</h1>
          <p className="text-gray-400">Let's customize your training. This takes 2 minutes.</p>
        </div>

        <div className="ninja-card mb-8">
          <div className="flex justify-between items-center mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  step >= i ? 'bg-green-500 text-black' : 'bg-white/10 text-gray-500'
                }`}>
                  {i}
                </div>
                {i < 3 && <div className={`w-24 h-1 mx-2 ${
                  step > i ? 'bg-green-500' : 'bg-white/10'
                }`} />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <h2 className="font-['Outfit'] text-2xl font-bold mb-4">What's your negotiation skill level?</h2>
              <RadioGroup value={skillLevel} onValueChange={setSkillLevel}>
                {['Beginner', 'Intermediate', 'Advanced'].map(level => (
                  <div key={level} className="flex items-center space-x-3 p-4 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`skill-${level.toLowerCase()}`}>
                    <RadioGroupItem value={level} id={level} />
                    <Label htmlFor={level} className="cursor-pointer text-lg">{level}</Label>
                  </div>
                ))}
              </RadioGroup>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!skillLevel}
                className="w-full mt-8 bg-green-600 hover:bg-green-500"
                data-testid="next-to-step-2"
              >
                Next <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <h2 className="font-['Outfit'] text-2xl font-bold mb-4">Which scenarios do you face? (Select all that apply)</h2>
              <div className="space-y-3">
                {scenarioOptions.map(scenario => (
                  <div key={scenario} className="flex items-center space-x-3 p-4 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`scenario-${scenario.toLowerCase().replace(/\//g, '-')}`}>
                    <Checkbox 
                      checked={scenarios.includes(scenario)} 
                      onCheckedChange={() => handleScenarioToggle(scenario)}
                      id={scenario}
                    />
                    <Label htmlFor={scenario} className="cursor-pointer text-lg">{scenario}</Label>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-8">
                <Button 
                  onClick={() => setStep(1)} 
                  variant="outline"
                  className="flex-1 border-white/20 hover:bg-white/10"
                  data-testid="back-to-step-1"
                >
                  Back
                </Button>
                <Button 
                  onClick={() => setStep(3)} 
                  disabled={scenarios.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-500"
                  data-testid="next-to-step-3"
                >
                  Next <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <h2 className="font-['Outfit'] text-2xl font-bold mb-4">What are your goals? (Select all that apply)</h2>
              <div className="space-y-3">
                {goalOptions.map(goal => (
                  <div key={goal} className="flex items-center space-x-3 p-4 rounded-lg hover:bg-white/5 cursor-pointer" data-testid={`goal-${goal.toLowerCase().replace(/ /g, '-')}`}>
                    <Checkbox 
                      checked={goals.includes(goal)} 
                      onCheckedChange={() => handleGoalToggle(goal)}
                      id={goal}
                    />
                    <Label htmlFor={goal} className="cursor-pointer text-lg">{goal}</Label>
                  </div>
                ))}
              </div>
              <div className="flex gap-4 mt-8">
                <Button 
                  onClick={() => setStep(2)} 
                  variant="outline"
                  className="flex-1 border-white/20 hover:bg-white/10"
                  data-testid="back-to-step-2"
                >
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={goals.length === 0}
                  className="flex-1 bg-green-600 hover:bg-green-500"
                  data-testid="complete-onboarding"
                >
                  Complete Setup
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;